import React from 'react';
import { ExternalLink } from 'lucide-react';
import { Link } from 'react-router';
import type { LscArchiveItem } from '../../types';
import {
  ARCHIVE_PLAYBACK_UNAVAILABLE_BODY,
  ARCHIVE_PLAYBACK_UNAVAILABLE_TITLE,
  TWITCH_VOD_EXPIRATION_COPY,
} from '../../archiveCopy';
import { isArchivePlaybackUnavailable } from '../../archivePlayback';
import { lscPath } from '../../routing';
import { getArchiveWatchVodVideoId } from '../ArchiveWatchVodSection';
import TwitchVodEmbed from '../TwitchVodEmbed';
import {
  YouTubeArchiveFrame,
  isYouTubeArchive,
  youtubeWatchUrl,
} from '../YouTubeArchiveFrame';
import { useLeague } from '../../hooks/useLeague';
import { formatArchiveCardMeta, getArchiveDisplayTitle } from './leagueHomeUtils';

export type LeagueHomeReplayPlayerProps = {
  leagueId: string;
  archive: LscArchiveItem | null;
};

const LeagueHomeReplayPlayer: React.FC<LeagueHomeReplayPlayerProps> = ({
  leagueId,
  archive,
}) => {
  const { league } = useLeague();
  if (!archive) {
    return (
      <section
        className="rounded-xl border border-dashed border-secondary-700 bg-secondary-900/30 p-6 text-center"
        data-testid="league-home-replay-player-empty"
      >
        <p className="text-sm text-secondary-400">Select a replay to watch here.</p>
      </section>
    );
  }

  const title = getArchiveDisplayTitle(archive);
  const meta = formatArchiveCardMeta(archive, league?.active_edition);
  const youtube = isYouTubeArchive(archive);
  const youtubeId = archive.provider_vod_id?.trim() || '';
  const videoId = getArchiveWatchVodVideoId(archive);
  const twitchUrl = archive.url?.trim() || null;
  const playbackUnavailable = isArchivePlaybackUnavailable(archive);
  const showEmbed = Boolean(!playbackUnavailable && (videoId || twitchUrl));

  return (
    <section
      className="space-y-4 rounded-xl border border-secondary-700 bg-secondary-800/50 p-4 sm:p-5 overflow-hidden max-w-full"
      data-testid="league-home-replay-player"
    >
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="space-y-1 min-w-0">
          <p className="text-xs font-semibold uppercase tracking-wide text-secondary-500">
            Now Playing
          </p>
          <h2 className="text-lg font-semibold text-white">{title}</h2>
          {meta ? (
            <p className="text-sm text-secondary-400" data-testid="league-home-replay-meta">
              {meta}
            </p>
          ) : null}
        </div>
        <Link
          to={lscPath(`/leagues/${leagueId}/archive/${archive.id}`)}
          className="btn-secondary shrink-0 text-xs"
          data-testid="league-home-replay-open-detail"
        >
          Open archive page
        </Link>
      </div>

      {playbackUnavailable ? (
        <div
          className="rounded-lg border border-secondary-700 bg-secondary-900/50 p-4 space-y-2"
          data-testid="league-home-replay-unavailable"
        >
          <p className="font-medium text-white">{ARCHIVE_PLAYBACK_UNAVAILABLE_TITLE}</p>
          <p className="text-sm text-secondary-400">{ARCHIVE_PLAYBACK_UNAVAILABLE_BODY}</p>
        </div>
      ) : youtube && youtubeId ? (
        <div className="max-w-full overflow-hidden rounded-lg">
          <YouTubeArchiveFrame videoId={youtubeId} title={title} />
        </div>
      ) : showEmbed ? (
        <div className="max-w-full overflow-hidden rounded-lg">
          <TwitchVodEmbed
            providerVodId={videoId ?? archive.provider_vod_id}
            embedUrl={archive.embed_url}
            embedParentDomains={archive.embed_parent_domains}
            title={title}
            thumbnailUrl={archive.thumbnail_url}
            twitchUrl={twitchUrl}
          />
        </div>
      ) : (
        <div
          className="rounded-lg border border-secondary-700 bg-secondary-900/50 p-4"
          data-testid="league-home-replay-unavailable"
        >
          <p className="text-sm text-secondary-400">
            This replay is not available to embed right now.
          </p>
        </div>
      )}

      {!playbackUnavailable && !youtube && twitchUrl ? (
        <a
          href={twitchUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-2 text-sm text-primary-300 hover:text-primary-200"
          data-testid="league-home-replay-open-twitch"
        >
          <ExternalLink className="h-4 w-4" aria-hidden />
          Watch on Twitch
        </a>
      ) : null}
      {!playbackUnavailable && youtube && youtubeWatchUrl(archive) ? (
        <a
          href={youtubeWatchUrl(archive) || undefined}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-2 text-sm text-primary-300 hover:text-primary-200"
          data-testid="league-home-replay-open-youtube"
        >
          <ExternalLink className="h-4 w-4" aria-hidden />
          Watch on YouTube
        </a>
      ) : null}

      {youtube ? null : (
        <p className="text-xs text-secondary-500">{TWITCH_VOD_EXPIRATION_COPY}</p>
      )}
    </section>
  );
};

export default LeagueHomeReplayPlayer;
